/**
 * AttractSlidesManager — admin UI to curate the Kiosk Attract slideshow.
 *
 * Supports three kinds of slides:
 *   - image:   uploaded via /api/images/upload (reuses the image library).
 *   - video:   uploaded via /api/kiosk/attract/upload-video.
 *   - product: references a menu product.
 *
 * Features: reorder (up/down arrows), enable/disable, edit duration & label.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Image as ImageIcon, Film, Package, Trash2, ArrowUp, ArrowDown, Upload, Plus,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const kindMeta = {
  image:   { label: 'Imagen',   Icon: ImageIcon, color: 'bg-amber-100 text-amber-800' },
  video:   { label: 'Vídeo',    Icon: Film,      color: 'bg-purple-100 text-purple-800' },
  product: { label: 'Producto', Icon: Package,   color: 'bg-emerald-100 text-emerald-800' },
};

const localizeName = (n) => {
  if (!n) return '';
  if (typeof n === 'string') return n;
  return n.es || n.cat || n.en || Object.values(n)[0] || '';
};

export const AttractSlidesManager = () => {
  const [slides, setSlides] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [productPick, setProductPick] = useState('');
  const fileImgRef = useRef(null);
  const fileVideoRef = useRef(null);

  const authHeaders = () => {
    const t = localStorage.getItem('admin_token') || '';
    return { 'Authorization': `Bearer ${t}` };
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API_URL}/api/kiosk/attract/all`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/products?limit=300`),
      ]);
      if (r1.ok) setSlides(await r1.json());
      if (r2.ok) setProducts(await r2.json());
    } catch (e) {
      toast.error('Error cargando slides');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const productLabel = useCallback((pid) => {
    const p = products.find(x => x.id === pid);
    return p ? localizeName(p.name) : '(producto eliminado)';
  }, [products]);

  /* ── Uploads ─────────────────────────────────────────────────────── */

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/images/upload`, {
        method: 'POST', body: fd, headers: authHeaders(),
      });
      if (!r.ok) throw new Error('upload');
      const img = await r.json();
      const create = await fetch(`${API_URL}/api/kiosk/attract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ kind: 'image', image_id: img.id, duration_ms: 6000 }),
      });
      if (!create.ok) throw new Error('create');
      toast.success('Imagen añadida al atraer');
      await fetchAll();
    } catch {
      toast.error('No se pudo subir la imagen');
    } finally {
      setBusy(false);
    }
  };

  const handleVideoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/kiosk/attract/upload-video`, {
        method: 'POST', body: fd, headers: authHeaders(),
      });
      if (!r.ok) throw new Error('upload');
      const v = await r.json();
      const create = await fetch(`${API_URL}/api/kiosk/attract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ kind: 'video', video_id: v.video_id, duration_ms: 8000 }),
      });
      if (!create.ok) throw new Error('create');
      toast.success('Vídeo añadido al atraer');
      await fetchAll();
    } catch {
      toast.error('No se pudo subir el vídeo (máx 60MB MP4/WebM/MOV)');
    } finally {
      setBusy(false);
    }
  };

  const addProductSlide = async () => {
    if (!productPick) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/kiosk/attract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ kind: 'product', product_id: productPick, duration_ms: 6000 }),
      });
      if (!r.ok) throw new Error();
      toast.success('Producto añadido al atraer');
      setShowProductPicker(false);
      setProductPick('');
      await fetchAll();
    } catch {
      toast.error('No se pudo añadir el producto');
    } finally {
      setBusy(false);
    }
  };

  /* ── Mutations ───────────────────────────────────────────────────── */

  const updateSlide = async (id, patch) => {
    // Optimistic local update so the input doesn't flicker.
    setSlides(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s));
    try {
      await fetch(`${API_URL}/api/kiosk/attract/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(patch),
      });
    } catch {
      toast.error('Error guardando');
      fetchAll();
    }
  };

  const deleteSlide = async (id) => {
    if (!window.confirm('¿Eliminar este slide?')) return;
    try {
      const r = await fetch(`${API_URL}/api/kiosk/attract/${id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!r.ok) throw new Error();
      toast.success('Slide eliminado');
      setSlides(prev => prev.filter(s => s.id !== id));
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const moveSlide = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= slides.length) return;
    const reordered = [...slides];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setSlides(reordered);
    try {
      await fetch(`${API_URL}/api/kiosk/attract/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ids: reordered.map(s => s.id) }),
      });
    } catch {
      toast.error('Error reordenando');
      fetchAll();
    }
  };

  /* ── Render ──────────────────────────────────────────────────────── */

  const renderThumb = (s) => {
    if (s.kind === 'image') {
      return <img src={`${API_URL}/api/images/file/${s.image_id}?w=160`} alt="" className="w-20 h-20 object-cover rounded" />;
    }
    if (s.kind === 'video') {
      return (
        <video
          src={`${API_URL}/api/kiosk/attract/video/${s.video_id}`}
          className="w-20 h-20 object-cover rounded bg-black"
          muted playsInline preload="metadata"
        />
      );
    }
    const prod = products.find(p => p.id === s.product_id);
    const img = prod?.image_url;
    if (img) return <img src={img} alt="" className="w-20 h-20 object-cover rounded" />;
    return <div className="w-20 h-20 rounded bg-gray-200 flex items-center justify-center"><Package className="text-gray-400" /></div>;
  };

  const renderLabel = (s) => {
    if (s.kind === 'product') return productLabel(s.product_id);
    return s.label || (s.kind === 'image' ? 'Imagen subida' : 'Vídeo subido');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Film size={20} className="text-stone-700" />
              Atraer (slides del Modo Kiosko)
            </CardTitle>
            <CardDescription>
              Curado de la pantalla pasiva que aparece tras N/2 minutos sin actividad.
              Sin slides custom, se usa la rotación automática de productos premium.
            </CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            <input
              ref={fileImgRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleImageFile}
              data-testid="attract-image-input"
            />
            <Button
              data-testid="attract-add-image-btn"
              variant="outline"
              onClick={() => fileImgRef.current?.click()}
              disabled={busy}
            >
              <Upload size={16} className="mr-1" /> Imagen
            </Button>
            <input
              ref={fileVideoRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              className="hidden"
              onChange={handleVideoFile}
              data-testid="attract-video-input"
            />
            <Button
              data-testid="attract-add-video-btn"
              variant="outline"
              onClick={() => fileVideoRef.current?.click()}
              disabled={busy}
            >
              <Upload size={16} className="mr-1" /> Vídeo
            </Button>
            <Button
              data-testid="attract-add-product-btn"
              variant="outline"
              onClick={() => setShowProductPicker(v => !v)}
              disabled={busy}
            >
              <Plus size={16} className="mr-1" /> Producto
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {showProductPicker && (
          <div className="mb-4 p-4 rounded-lg border bg-gray-50 flex gap-3 items-center" data-testid="attract-product-picker">
            <div className="flex-1">
              <Select value={productPick} onValueChange={setProductPick}>
                <SelectTrigger data-testid="attract-product-select">
                  <SelectValue placeholder="Elige un producto" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {localizeName(p.name)} — {Number(p.price || 0).toFixed(2)}€
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={addProductSlide} disabled={!productPick || busy} data-testid="attract-product-confirm">
              Añadir
            </Button>
            <Button variant="ghost" onClick={() => { setShowProductPicker(false); setProductPick(''); }}>
              Cancelar
            </Button>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-gray-500">Cargando...</p>
        ) : slides.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500" data-testid="attract-empty-state">
            Sin slides custom. El atraer mostrará automáticamente productos premium.
          </div>
        ) : (
          <div className="space-y-2" data-testid="attract-slides-list">
            {slides.map((s, idx) => {
              const meta = kindMeta[s.kind] || kindMeta.product;
              const Icon = meta.Icon;
              return (
                <div
                  key={s.id}
                  data-testid={`attract-slide-${s.id}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border ${s.enabled ? 'bg-white' : 'bg-gray-50 opacity-60'}`}
                >
                  <div className="flex flex-col gap-1">
                    <Button
                      size="icon" variant="ghost"
                      onClick={() => moveSlide(idx, -1)} disabled={idx === 0}
                      data-testid={`attract-slide-up-${s.id}`}
                      className="h-6 w-6"
                    >
                      <ArrowUp size={14} />
                    </Button>
                    <Button
                      size="icon" variant="ghost"
                      onClick={() => moveSlide(idx, 1)} disabled={idx === slides.length - 1}
                      data-testid={`attract-slide-down-${s.id}`}
                      className="h-6 w-6"
                    >
                      <ArrowDown size={14} />
                    </Button>
                  </div>

                  {renderThumb(s)}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge className={meta.color}>
                        <Icon size={12} className="mr-1" />
                        {meta.label}
                      </Badge>
                      <span className="font-medium truncate">{renderLabel(s)}</span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap text-sm">
                      {s.kind !== 'video' && (
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-gray-600 whitespace-nowrap">Duración (s):</Label>
                          <Input
                            type="number" min={2} max={30}
                            value={Math.round(s.duration_ms / 1000)}
                            onChange={(e) => {
                              const sec = Math.max(2, Math.min(30, Number(e.target.value) || 6));
                              updateSlide(s.id, { duration_ms: sec * 1000 });
                            }}
                            className="w-20 h-8"
                            data-testid={`attract-slide-duration-${s.id}`}
                          />
                        </div>
                      )}
                      {s.kind === 'video' && (
                        <span className="text-xs text-gray-500 italic">Duración: la del vídeo</span>
                      )}
                    </div>
                  </div>

                  <Switch
                    checked={s.enabled}
                    onCheckedChange={(v) => updateSlide(s.id, { enabled: v })}
                    data-testid={`attract-slide-toggle-${s.id}`}
                  />
                  <Button
                    size="icon" variant="ghost"
                    onClick={() => deleteSlide(s.id)}
                    data-testid={`attract-slide-delete-${s.id}`}
                  >
                    <Trash2 size={16} className="text-red-600" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AttractSlidesManager;
